<template>
  <div class="docs-view">
    <div class="docs-layout">
      <aside class="docs-sidebar">
        <div class="docs-sidebar__inner">
          <label class="docs-search">
            <span class="sr-only">Search documentation</span>
            <input
              v-model.trim="search"
              type="search"
              placeholder="Search sections..."
              class="docs-search__input"
            />
          </label>
          <div v-if="filteredSections.length" class="docs-nav-groups">
            <div v-for="sec in filteredSections" :key="sec.key" class="docs-nav-group">
              <div class="docs-nav-group__title">{{ sec.title }}</div>
              <nav class="docs-nav">
                <button
                  v-for="item in sec.items"
                  :key="item.slug"
                  @click="active = item.slug"
                  :class="['docs-nav__item', active === item.slug ? 'docs-nav__item--active' : '']"
                  v-html="formatLabel(item.frontMatter.navTitle)"
                ></button>
              </nav>
            </div>
          </div>
          <div v-else class="docs-empty">No matches. Try a different term.</div>
        </div>
      </aside>
      <section class="docs-content">
        <header class="docs-header">
          <div>
            <p class="docs-breadcrumb">Documentation</p>
            <h1 class="docs-title" v-html="currentTitleHtml"></h1>
            <p v-if="currentSummaryHtml" class="docs-summary" v-html="currentSummaryHtml"></p>
          </div>
          <div v-if="activeItem" class="docs-meta">
            <span v-if="activeItem.frontMatter.status !== 'published'" class="docs-chip timing">
              {{ activeItem.frontMatter.status.replace('-', ' ') }}
            </span>
            <span v-if="activeItem.frontMatter.updated" class="docs-chip">
              Updated {{ activeItem.frontMatter.updated }}
            </span>
          </div>
        </header>
        <div class="docs-body markdown-body" v-html="currentHtml"></div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, watchEffect, nextTick, onMounted } from 'vue'
import { marked } from 'marked'
import hljs from 'highlight.js'
import 'highlight.js/styles/github-dark.css'
import DOMPurify from 'dompurify'
import mermaid from 'mermaid'
import { useHead } from '@vueuse/head'
import { createLogger } from '@/lib/logger'

const log = createLogger('Docs')
import {
  docSections,
  docsBySlug,
  type DocManifestSection,
  type DocManifestItem,
} from '@/lib/docsManifest'

const escapeHtml = (input: unknown) =>
  String(input ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

const getLanguage = (value: unknown) => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim().toLowerCase()
  return trimmed.length ? trimmed : undefined
}

const renderer = new marked.Renderer()

renderer.code = (code: string, info?: string) => {
  const lang = getLanguage(info?.split(/\s+/)[0])
  const isSupported = lang && hljs.getLanguage(lang)

  const rawCode = (() => {
    if (typeof code === 'string') return code
    if (code && typeof code === 'object' && 'text' in (code as Record<string, unknown>)) {
      return String((code as Record<string, unknown>).text ?? '')
    }
    return String(code ?? '')
  })()

  if (lang === 'mermaid') {
    return `<figure class="code-block docs-mermaid" data-lang="mermaid"><pre class="hljs"><code class="hljs language-mermaid">${escapeHtml(rawCode)}</code></pre><figcaption>MERMAID</figcaption></figure>`
  }

  let highlighted: string

  try {
    highlighted = isSupported
      ? hljs.highlight(rawCode, { language: lang!, ignoreIllegals: true }).value
      : hljs.highlightAuto(rawCode).value
  } catch {
    highlighted = escapeHtml(rawCode)
  }

  const className = isSupported ? `language-${lang}` : 'language-plaintext'
  const label = lang ?? 'plain'
  return `<figure class="code-block" data-lang="${label}"><pre class="hljs"><code class="hljs ${className || ''}">${highlighted}</code></pre><figcaption>${label.toUpperCase()}</figcaption></figure>`
}

marked.setOptions({
  gfm: true,
  breaks: true,
  langPrefix: 'language-',
  headerIds: false,
  mangle: false,
  renderer,
})

mermaid.initialize({
  startOnLoad: false,
  theme: 'dark',
  securityLevel: 'strict',
})

const sections = computed(() => docSections as DocManifestSection[])

const active = ref<string>(docSections[0]?.items[0]?.slug ?? '')
const search = ref('')

const flatItems = computed(() => sections.value.flatMap((section) => section.items))

const filteredSections = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return sections.value

  return sections.value
    .map((sec) => ({
      ...sec,
      items: sec.items.filter((item) => {
        const haystack =
          `${item.frontMatter.navTitle} ${item.frontMatter.summary ?? ''} ${item.slug}`.toLowerCase()
        return haystack.includes(term)
      }),
    }))
    .filter((sec) => sec.items.length)
})

const activeItem = computed<DocManifestItem | undefined>(() => docsBySlug[active.value])

watch(
  filteredSections,
  (secList) => {
    const exists = secList.some((sec) => sec.items.some((item) => item.slug === active.value))
    if (!exists) {
      const fallback = secList[0]?.items[0]
      if (fallback) active.value = fallback.slug
    }
  },
  { immediate: true },
)

const currentMarkdown = computed(() => {
  const item = activeItem.value
  return item ? item.content : '# Select a document'
})

const currentTitle = computed(() => {
  const item = activeItem.value
  return item?.frontMatter.title ?? 'Documentation'
})

const currentSummaryHtml = computed(() => {
  const summary = activeItem.value?.frontMatter.summary
  if (!summary) return ''
  return DOMPurify.sanitize(marked.parseInline(summary))
})

function formatLabel(text: string): string {
  const cleaned = text.replace(/^Your\s+/i, '')
  return DOMPurify.sanitize(marked.parseInline(cleaned.replace(/TestDrive/g, 'Test*Drive*')))
}

const currentTitleHtml = computed(() => formatLabel(currentTitle.value))

const currentHtml = computed(() => DOMPurify.sanitize(marked.parse(currentMarkdown.value)))

async function renderMermaid() {
  await nextTick()

  const containers = document.querySelectorAll<HTMLElement>('.docs-body .docs-mermaid pre code')
  if (!containers.length) return

  await Promise.all(
    Array.from(containers).map(async (codeEl, index) => {
      const parent = codeEl.closest('figure.docs-mermaid')
      if (!parent) return
      const graphDefinition = codeEl.textContent ?? ''
      try {
        const { svg } = await mermaid.render(`mermaid-${index}-${active.value}`, graphDefinition)
        const sanitizedSvg = DOMPurify.sanitize(svg, {
          USE_PROFILES: { svg: true, svgFilters: true },
          ADD_TAGS: [
            'foreignObject',
            'animate',
            'marker',
            'pattern',
            'style',
            'defs',
            'linearGradient',
            'stop',
          ],
          ADD_ATTR: [
            'class',
            'style',
            'fill',
            'stroke',
            'stroke-width',
            'stroke-linecap',
            'stroke-linejoin',
            'marker-end',
            'marker-start',
            'd',
            'points',
            'viewBox',
            'transform',
            'x',
            'y',
            'x1',
            'x2',
            'y1',
            'y2',
            'cx',
            'cy',
            'r',
            'rx',
            'ry',
            'width',
            'height',
            'font-size',
            'font-weight',
            'text-anchor',
            'dominant-baseline',
            'id',
            'href',
            'xlink:href',
            'gradientUnits',
            'offset',
            'stop-color',
            'stop-opacity',
          ],
        }) as string
        parent.innerHTML = sanitizedSvg
        parent.classList.add('docs-mermaid--rendered')
      } catch (error) {
        log.error('Mermaid render failed', error)
      }
    }),
  )
}

watch(currentHtml, () => {
  renderMermaid()
})

onMounted(() => {
  renderMermaid()
})

watchEffect(() => {
  useHead({
    title: `${currentTitle.value} · Routr Docs`,
  })
})
</script>

<style scoped>
.docs-view {
  padding: 2rem 2.5rem;
  margin-top: -0.625rem;
}

.docs-layout {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: minmax(260px, 18rem) 1fr;
}

@media (max-width: 1024px) {
  .docs-view {
    padding: 1.5rem 1.25rem;
  }
  .docs-layout {
    grid-template-columns: 1fr;
  }
}

.docs-sidebar {
  position: relative;
}

.docs-sidebar__inner {
  position: sticky;
  top: 1.5rem;
  max-height: calc(100vh - 3rem);
  overflow: auto;
  border-radius: 1rem;
  padding: 1.25rem;
  background: linear-gradient(160deg, rgba(15, 23, 42, 0.72), rgba(30, 41, 59, 0.52));
  backdrop-filter: blur(16px);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.45);
}

.docs-search__input {
  width: 100%;
  background: rgba(15, 23, 42, 0.65);
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 0.75rem;
  padding: 0.65rem 0.9rem;
  color: #f8fafc;
  font-size: 0.9rem;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.docs-search__input::placeholder {
  color: rgba(148, 163, 184, 0.7);
}

.docs-search__input:focus {
  outline: none;
  border-color: rgba(96, 165, 250, 0.6);
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.25);
}

.docs-nav-groups {
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.docs-nav-group__title {
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(203, 213, 225, 0.7);
  margin-bottom: 0.6rem;
}

.docs-nav {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.docs-nav__item {
  text-align: left;
  width: 100%;
  border-radius: 0.65rem;
  padding: 0.55rem 0.75rem;
  font-size: 0.92rem;
  color: rgba(226, 232, 240, 0.78);
  background: transparent;
  transition:
    background 0.2s ease,
    color 0.2s ease;
}

.docs-nav__item:hover {
  background: rgba(148, 163, 184, 0.18);
  color: #f8fafc;
}

.docs-nav__item--active {
  background: linear-gradient(120deg, rgba(96, 165, 250, 0.38), rgba(59, 130, 246, 0.28));
  color: #fff;
  box-shadow: 0 8px 18px rgba(59, 130, 246, 0.25);
}

.docs-empty {
  margin-top: 1.25rem;
  font-size: 0.9rem;
  color: rgba(148, 163, 184, 0.9);
}

.docs-content {
  border-radius: 1rem;
  padding: 2rem 2.25rem;
  background: linear-gradient(160deg, rgba(30, 41, 59, 0.7), rgba(15, 23, 42, 0.88));
  backdrop-filter: blur(18px);
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 16px 45px rgba(15, 23, 42, 0.5);
  max-height: calc(100vh - 3rem);
  overflow: auto;
}

@media (max-width: 1024px) {
  .docs-sidebar__inner {
    position: static;
    max-height: none;
  }
  .docs-content {
    max-height: none;
  }
}

.docs-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 2rem;
}

.docs-breadcrumb {
  text-transform: uppercase;
  letter-spacing: 0.24em;
  font-size: 0.68rem;
  color: rgba(148, 163, 184, 0.7);
  margin-bottom: 0.4rem;
}

.docs-title {
  font-size: 1.85rem;
  font-weight: 700;
  color: #f8fafc;
}

.docs-summary {
  margin-top: 0.6rem;
  color: rgba(226, 232, 240, 0.78);
  font-size: 0.95rem;
  max-width: 72ch;
}

.docs-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  background: rgba(96, 165, 250, 0.18);
  border: 1px solid rgba(96, 165, 250, 0.28);
  color: rgba(191, 219, 254, 0.95);
  font-size: 0.8rem;
  letter-spacing: 0.02em;
}

.docs-chip.timing {
  background: rgba(249, 115, 22, 0.18);
  border-color: rgba(249, 115, 22, 0.28);
  color: rgba(253, 186, 140, 0.95);
}

.docs-body {
  color: #e5e7eb;
  line-height: 1.7;
  font-size: 0.98rem;
}

.markdown-body :deep(h1) {
  font-size: 1.6rem;
  font-weight: 700;
  margin: 1.25rem 0 0.75rem;
}

.markdown-body :deep(h2) {
  font-size: 1.3rem;
  font-weight: 700;
  margin: 1.25rem 0 0.65rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  padding-bottom: 0.4rem;
}

.markdown-body :deep(h3) {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 1rem 0 0.55rem;
}

.markdown-body :deep(p) {
  margin: 0.65rem 0;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  padding-left: 1.25rem;
  margin: 0.65rem 0;
}

.markdown-body :deep(li + li) {
  margin-top: 0.35rem;
}

.markdown-body :deep(code) {
  background: rgba(148, 163, 184, 0.15);
  padding: 0.18rem 0.35rem;
  border-radius: 0.4rem;
  font-size: 0.9rem;
}

.markdown-body :deep(pre),
.markdown-body :deep(.hljs) {
  background: #0f172a;
  padding: 1rem 1.1rem;
  border-radius: 0.75rem;
  overflow: auto;
  border: 1px solid rgba(15, 23, 42, 0.9);
}

.markdown-body :deep(.code-block) {
  margin: 1rem 0;
  background: rgba(15, 23, 42, 0.9);
  border-radius: 0.85rem;
  border: 1px solid rgba(96, 165, 250, 0.22);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.45);
}

.markdown-body :deep(.code-block pre) {
  margin: 0;
  border: none;
  border-radius: 0.85rem 0.85rem 0 0;
}

.markdown-body :deep(.code-block figcaption) {
  margin: 0;
  padding: 0.45rem 0.9rem;
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(191, 219, 254, 0.75);
  border-top: 1px solid rgba(96, 165, 250, 0.26);
  border-radius: 0 0 0.85rem 0.85rem;
  background: rgba(37, 99, 235, 0.12);
}

.markdown-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.95rem;
}

.markdown-body :deep(th),
.markdown-body :deep(td) {
  border: 1px solid rgba(148, 163, 184, 0.25);
  padding: 0.75rem;
  vertical-align: top;
}

.markdown-body :deep(thead th) {
  background: rgba(148, 163, 184, 0.1);
  color: #cbd5f5;
}

.markdown-body :deep(blockquote) {
  border-left: 4px solid rgba(96, 165, 250, 0.6);
  padding-left: 1rem;
  color: rgba(226, 232, 240, 0.85);
  font-style: italic;
  margin: 1rem 0;
}

.markdown-body :deep(a) {
  color: rgba(96, 165, 250, 0.9);
  text-decoration: underline;
  text-decoration-thickness: 1px;
}

.markdown-body :deep(img) {
  max-width: 100%;
  border-radius: 0.75rem;
  border: 1px solid rgba(148, 163, 184, 0.2);
  margin: 1rem 0;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
